import { css } from '@emotion/react'
import { colors, font } from 'plume-ui'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { useSortable, SortableContext } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { arrayMoveImmutable } from 'array-move'
import { ConditionName } from '../actions'
import {
  COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS,
  UNREMOVABLE_NAMES,
} from '../reducers/column'

type Props = {
  selected: string[]
  onRemove: (cond: string) => any
  onSort: (conds: string[]) => any
}

export const ColumnSort = ({ selected, onRemove, onSort }: Props) => {
  const onSortEnd = (oldIndex: number, newIndex: number) => {
    onSort(arrayMoveImmutable(selected, oldIndex, newIndex))
  }

  return (
    <div
      className="body column-sort"
      css={css`
        float: right;
        padding: 15px 0 0 15px;
      `}
    >
      <span
        className="description"
        css={css`
          color: #666;
          font-size: 13px;
        `}
      >
        並べ替え
      </span>
      <span
        className="annotation"
        css={css`
          color: #999;
          float: right;
          font-size: 13px;
        `}
      >
        ドラッグ＆ドロップで並べ替え
      </span>
      <SortableList
        selected={selected}
        onRemove={onRemove}
        onSortEnd={onSortEnd}
      />
    </div>
  )
}

type SortableListProps = {
  selected: string[]
  onRemove: (cond: string) => any
  onSortEnd: (oldIndex: number, newIndex: number) => void
}

const SortableList: React.FC<SortableListProps> = ({
  selected,
  onRemove,
  onSortEnd,
}) => {
  const getIndex = (name: string) => selected.findIndex((item) => item === name)
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) {
      onSortEnd(getIndex(active.id as string), getIndex(over.id as string))
    }
  }
  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
      <SortableContext items={selected}>
        <ul
          className="options"
          css={css`
            background-color: #fff;
            border: 1px solid #d9d9d9;
            border-radius: 2px;
            overflow-y: auto;
            margin-top: 6px;
            width: 400px;
            height: 345px;
            padding: 0;
          `}
        >
          {selected.map((name, i) => (
            <SortableItem
              name={name as ConditionName}
              isUnremoval={UNREMOVABLE_NAMES.includes(name)}
              onRemove={(name: any) => onRemove(name)}
              key={`${i}-${name}`}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

type SortableItemProps = {
  name: string
  isUnremoval: boolean
  onRemove: (cond: string) => any
}

const SortableItem: React.FC<SortableItemProps> = ({
  name,
  isUnremoval,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: name })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li
      className="sortable-item"
      css={css`
        background-color: #fff;
        border-bottom: 1px solid #d9d9d9;
        padding-top: 7px;
        padding-bottom: 7px;
        position: relative;
        z-index: 1000;
        font-size: 14px;
        padding-left: 10px;
        list-style: none;
        text-align: left;
      `}
      ref={setNodeRef}
      {...attributes}
      style={style}
    >
      <i
        css={css`
          color: ${colors.red50};
          cursor: pointer;
          font-size: 16px;
          margin-right: 6px;
          vertical-align: middle;
          &.disabled {
            color: ${colors.gray50};
            cursor: default;
          }
        `}
        className={
          isUnremoval
            ? 'icon-remove icomoon icon-remove-fill disabled'
            : 'icon-remove icomoon icon-remove-fill'
        }
        onClick={() => (isUnremoval ? null : onRemove(name))}
      />
      {
        // @ts-ignore
        COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.label.replace('\n', '') ??
          ''
      }
      <DragHandle listeners={listeners} />
    </li>
  )
}

type DragHandleProps = {
  listeners?: SyntheticListenerMap
}

const DragHandle: React.FC<DragHandleProps> = ({ listeners }) => (
  <i
    className="icon-sort icomoon icon-sort1"
    css={css`
      color: #d9d9d9;
      cursor: pointer;
      font-weight: ${font.weight.bold};
      font-size: 18px;
      position: absolute;
      right: 5px;
    `}
    {...listeners}
  />
)
